import React, { useState } from 'react'

import { OtherToggleLabels } from '../_shared/OtherExpand'
import { ChoiceBarsPlot, ChoiceBarsPlotData } from './ChoiceBarsPlot'
import { ShareDonutPlot, ShareDonutPlotData } from './ShareDonutPlot'

import { PlotHighlight, PlotRenderMode } from '@digital-twins/slide-schemas/generated/plot-types'

/**
 * A choice read on the summary card, the survey tab and quick-study (plots
 * plan J): the choice-bars plot, and the bars / donut toggle when Python
 * stamped a share-donut alternative (single select, at most six rows). The
 * toggle only switches the view; both views draw the same stamped figures.
 * The deck never shows it (a slide is static).
 */

export type ChoicePlotViewMode = 'choice-bars' | 'share-donut'

export interface ChoicePlotToggleLabels {
  bars: string
  donut: string
}

export interface ChoicePlotViewProps {
  data: ChoiceBarsPlotData
  /** The stamped share-donut alternative's data, when the producer offered one. */
  donut?: ShareDonutPlotData | null
  /** The toggle's words in the viewer's UI language; no toggle without them. */
  toggleLabels?: ChoicePlotToggleLabels | null
  initialMode?: ChoicePlotViewMode
  /** A note printed above the plot (e.g. the scale's "1-5 scale"). */
  note?: string | null
  /** The stamped render prop: `none` on an ordinal read (a scale, the NPS bands). */
  highlight?: PlotHighlight
  renderMode?: PlotRenderMode
  locale?: string | null
  /** Legacy stored payloads print today's figure string; new payloads never set it. */
  formatShare?: (share: number) => string
  /** Survey tab: print stamped counts on the bars. The donut stays on percent. */
  shareUnit?: 'percent' | 'count'
  /** Legacy stored cards only: the caller's label formatter (the frontend's `formatLabel`). */
  formatItemLabel?: (label: string) => string
  testIdPrefix?: string
  /** The bars' "Other" link words (decision 23); the donut keeps its hover list. */
  otherToggleLabels?: OtherToggleLabels | null
}

/** The share-donut alternative's data from a stamped `alternatives` list, if any. */
export function donutAlternative(alternatives: unknown): ShareDonutPlotData | null {
  if (!Array.isArray(alternatives)) {
    return null
  }
  const found = alternatives.find(
    (entry) => typeof entry === 'object' && entry !== null && (entry as { kind?: unknown }).kind === 'share-donut',
  ) as { data?: unknown } | undefined
  const data = found?.data
  if (typeof data !== 'object' || data === null || !Array.isArray((data as { slices?: unknown }).slices)) {
    return null
  }
  return data as ShareDonutPlotData
}

export function ChoicePlotView(props: ChoicePlotViewProps) {
  const { data, donut, toggleLabels, initialMode = 'choice-bars', note, highlight, renderMode, locale, formatShare, shareUnit, formatItemLabel, testIdPrefix = 'choice-plot', otherToggleLabels } = props

  const [mode, setMode] = useState<ChoicePlotViewMode>(donut ? initialMode : 'choice-bars')

  const hasToggle = Boolean(donut && toggleLabels)
  const showDonut = hasToggle && mode === 'share-donut' && donut
  const toggle = hasToggle ? (
    <div role="group" className="flex justify-end gap-1" data-testid="plot-alternative-toggle">
      {(['choice-bars', 'share-donut'] as Array<ChoicePlotViewMode>).map((option) => (
        <button
          key={option}
          type="button"
          aria-pressed={mode === option}
          data-testid={`plot-alternative-${option}`}
          onClick={() => setMode(option)}
          className={
            mode === option
              ? 'rounded-md border border-border bg-muted px-2 py-0.5 text-xs text-foreground'
              : 'rounded-md border border-transparent px-2 py-0.5 text-xs text-muted-foreground hover:text-foreground'
          }
        >
          {option === 'choice-bars' ? toggleLabels?.bars : toggleLabels?.donut}
        </button>
      ))}
    </div>
  ) : null

  return (
    <div className="flex w-full flex-col gap-3" data-testid={testIdPrefix}>
      {toggle}
      {showDonut ? (
        <ShareDonutPlot data={donut} renderMode={renderMode} locale={locale} testIdPrefix={`${testIdPrefix}-donut`} />
      ) : (
        <ChoiceBarsPlot
          data={data}
          size="card"
          highlight={highlight}
          note={note}
          renderMode={renderMode}
          locale={locale}
          formatShare={formatShare}
          shareUnit={shareUnit}
          formatItemLabel={formatItemLabel}
          testIdPrefix={`${testIdPrefix}-bars`}
          otherToggleLabels={otherToggleLabels}
        />
      )}
    </div>
  )
}
